/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { useEffect, useRef, useState } from "react";
import { RichText } from "@simplicitycms/simplicity-richtext";
import { transformRichTextBulletList } from "./utils";
import { ReactComponent as CheckmarkIcon } from "cms/assets/icons/checkmark.svg";

const FeatureAccordionComponent = (props) => {
    const component = props?.component;
    const title = component.title;
    const subtitle = component.subtitle;
    const description = component.description;
    const sections = component.sections || [];
    const willAnimate = false;
    // TODO: make animation a component setting
    // const willAnimate = component.willAnimate;

    const pausedRef = useRef(false);
    const sectionRef = useRef(0);
    const containerRef = useRef(null);
    const enableRef = useRef(false);

    const sectionItems = sections.map((section) => {
        return {
            title: section.title,
            description: section.description,
            items: transformRichTextBulletList(section),
            image: section.image
        };
    });

    const [paused, setPaused] = useState(sections[0]?.image?.url || null);
    const [image, setImage] = useState(sections[0]?.image?.url || null);
    const [expandedSection, setExpandedSection] = useState(0); // First section expanded by default

    const onChangeImage = (section) => {
        const imageUrl = section?.image?.url;
        setImage(imageUrl);
    };

    const handleSectionClick = (index, section) => {
        setExpandedSection(expandedSection === index ? -1 : index); // Toggle or set new expanded section
        onChangeImage(section);
    };

    // Pause animation when mouse is over component
    const handleMouseEnter = () => {
        pausedRef.current = true;
        setPaused(true);
    };

    // Pause animation when mouse is over component
    const handleMouseLeave = () => {
        pausedRef.current = false;
        setPaused(false);
    };

    // Animation Controller
    useEffect(() => {
        let intervalId;
        if (!enableRef.current && willAnimate) {
            enableRef.current = true;
            intervalId = setInterval(() => {
                if (pausedRef.current) {
                    return;
                }
                let numberOfSections = 0;
                if (containerRef.current) {
                    const childDivs = Array.from(containerRef.current?.children);
                    numberOfSections = childDivs.length;
                }
                if (numberOfSections > 0) {
                    if (sectionRef.current < numberOfSections - 1) {
                        sectionRef.current++;
                    } else if (sectionRef.current === numberOfSections - 1) {
                        sectionRef.current = 0;
                    } else {
                        sectionRef.current = 0;
                    }
                    setExpandedSection( sectionRef.current );
                    const img = sectionItems[sectionRef.current]?.image?.url;
                    setImage(img);
                }
            }, 5000);
        }
        return () => {
            if (intervalId) {
                clearInterval(intervalId);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div
            className={`feature-accordion-display ${paused ? "paused" : "running"}`}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
        >
            <div className="feature-accordion-display-title">
                <h2>{title}</h2>
            </div>

            <div className="feature-accordion-display-subtitle">
                <h3>{subtitle}</h3>
            </div>
            <div className="feature-accordion-display-description">
                <RichText richtext={description} />
            </div>
            <div className="feature-accordion-display-container">
                <div className="feature-accordion-display-container-left">
                    <img className="feature-accordion-display-container-left-image" src={image} alt={title} />
                </div>
                <div className="feature-accordion-display-container-right" ref={containerRef}>
                    {sectionItems.map((section, index) => (
                        <div
                            key={section?.title}
                            className={`feature-accordion-display-section ${expandedSection === index ? "expanded" : "collapsed"}`}
                        >
                            <button
                                className="feature-accordion-display-section-title"
                                onClick={() => handleSectionClick(index, section)}
                                aria-expanded={expandedSection === index}
                                type="button"
                            >
                                {section?.items?.title}
                            </button>
                            <div className="feature-accordion-display-section-content">
                                <p className="feature-accordion-display-section-description">{section?.items?.description}</p>
                                <div className="feature-accordion-display-section-list">
                                    {section?.items?.bulletList.map((item) => (
                                        <div key={item} className="feature-accordion-display-section-list-item">
                                            <CheckmarkIcon className="feature-accordion-display-section-list-icon" />
                                            <span className="feature-accordion-display-section-list-text">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export { FeatureAccordionComponent };
